import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { ParticleField } from "@/components/site/ParticleField";
import { Logo } from "@/components/site/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGIN } from "@/constants/testIds/auth";

export default function Connexion() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form);
      toast.success("Bienvenue dans votre espace client.");
      navigate("/espace-client");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-night px-5 py-32" data-testid="login-page">
      <ParticleField className="absolute inset-0 h-full w-full" density={50} />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size="h-14" />
          <h1 className="mt-6 font-display text-3xl text-white">Espace client</h1>
          <p className="mt-2 text-white/60 font-body text-sm">Suivez vos dossiers, factures et support prioritaire.</p>
        </div>
        <form onSubmit={submit} className="rounded-3xl border border-gold/25 bg-navy/60 backdrop-blur-xl p-8">
          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input id="email" type="email" data-testid={LOGIN.emailInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@email.com" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80">Mot de passe</Label>
              <Input id="password" type="password" data-testid={LOGIN.passwordInput} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/40" required />
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid={LOGIN.submitButton} className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3.5 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>Se connecter <ArrowRight size={16} /></>}
          </button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/40"><Lock size={12} /> Connexion sécurisée</p>
        </form>
        <p className="mt-6 text-center text-sm text-white/60 font-body">
          Pas encore de compte ?{" "}
          <Link to="/inscription" data-testid={LOGIN.registerLink} className="text-gold font-semibold hover:underline">Créer un espace</Link>
        </p>
      </motion.div>
    </section>
  );
}
